/**
 * connection.js handleError resolves failures as { success: false, error }
 * (error: the axios error) instead of rejecting, so any wrapper that types its
 * success payload has to union this in and callers must narrow before reading.
 *
 * The api has two 400 bodies: zod rejections are { error: "Validation error",
 * details } and service errors are { message }.
 */
export type ConnectionFailurePayload = {
  success: false;
  error?: {
    response?: {
      status?: number;
      data?: { message?: string; error?: string };
    };
  };
};

export const isConnectionFailure = (
  payload: unknown
): payload is ConnectionFailurePayload =>
  !!payload &&
  typeof payload === "object" &&
  (payload as { success?: unknown }).success === false;

/** A successful list read: not a connection failure, and `data` is an array. */
export const hasDataArray = (
  payload: unknown
): payload is { data: unknown[] } =>
  !!payload &&
  typeof payload === "object" &&
  !isConnectionFailure(payload) &&
  Array.isArray((payload as { data?: unknown }).data);

// Reads `message` (service 400s) before `error` (zod 400s).
export const connectionFailureMessage = (
  payload: ConnectionFailurePayload
): string => {
  const data = payload.error?.response?.data;
  return data?.message ?? data?.error ?? "request failed";
};
